import { useEffect, useRef, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { useWizard } from 'react-use-wizard';
import { AnimatePresence, motion } from 'framer-motion';

import {
  PREMIUM_BUDGET_STEP_ID,
  type FlatStepConfig,
} from '@/features/guided-financial-planning/config';
import { useGuidedPlanningFlatStepConfigs } from '@/features/guided-financial-planning/context';

function getStepIndexFromPath(pathname: string, configs: FlatStepConfig[]): number {
  const pathSegments = pathname.split('/').filter(Boolean);
  const currentStepPath = pathSegments[pathSegments.length - 1] ?? '';
  const rawIndex = configs.findIndex(step => step.path === currentStepPath);
  if (rawIndex >= 0) {
    return rawIndex;
  }
  if (
    currentStepPath === PREMIUM_BUDGET_STEP_ID &&
    !configs.some(s => s.id === PREMIUM_BUDGET_STEP_ID)
  ) {
    const quotesLoading = configs.findIndex(s => s.id === 'quotes-loading');
    if (quotesLoading >= 0) {
      return quotesLoading;
    }
    const optionsIdx = configs.findIndex(s => s.id === 'options');
    return optionsIdx >= 0 ? optionsIdx : 0;
  }
  return 0;
}

/**
 * Syncs the wizard's internal step state with browser navigation (back/forward).
 * Called inside the wizard wrapper to access useWizard context.
 */
function useWizardUrlSync() {
  const { activeStep, goToStep } = useWizard();
  const location = useLocation();
  const flatStepConfigs = useGuidedPlanningFlatStepConfigs();
  const isInitialMount = useRef(true);

  useEffect(() => {
    // Skip on initial mount - the wizard's startIndex handles that
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    const urlStepIndex = getStepIndexFromPath(location.pathname, flatStepConfigs);
    if (urlStepIndex !== activeStep) {
      goToStep(urlStepIndex);
    }
  }, [location.pathname, activeStep, goToStep, flatStepConfigs]);
}

function scrollToTop() {
  document.querySelector('body')?.scrollTo(0, 0);
  window.scrollTo(0, 0);
}

interface AnimatedStepProps {
  /** Unique key for the current step/substep (triggers animation on change) */
  stepKey: number;
  children?: ReactNode;
}

/**
 * Animated wrapper for step/substep transitions.
 * Use with wizard steps or substeps for consistent slide animations.
 * Automatically scrolls to top when step changes.
 */
export function AnimatedStep({ stepKey, children }: AnimatedStepProps) {
  const previousKeyRef = useRef(stepKey);

  // Calculate direction: 1 = forward, -1 = backward
  const direction = stepKey > previousKeyRef.current ? 1 : -1;

  // Update ref and scroll to top when step changes
  if (previousKeyRef.current !== stepKey) {
    previousKeyRef.current = stepKey;
  }

  // Scroll to top when step changes
  useEffect(() => {
    scrollToTop();
  }, [stepKey]);

  return (
    <AnimatePresence
      mode="wait"
      initial={false}
    >
      <motion.div
        key={stepKey}
        initial={{ opacity: 0, y: direction > 0 ? 10 : -10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: direction > 0 ? -10 : 10 }}
        transition={{ duration: 0.2, ease: 'easeInOut' }}
      >
        {children}
      </motion.div>
    </AnimatePresence>
  );
}

interface WizardAnimatedStepProps {
  children?: ReactNode;
}

/**
 * Animated wrapper for react-use-wizard steps.
 * Uses useWizard internally to track step changes.
 * Also syncs wizard state with browser navigation (back/forward).
 * Pass to Wizard's `wrapper` prop.
 */
export function WizardAnimatedStep({ children }: WizardAnimatedStepProps) {
  const { activeStep } = useWizard();

  // Sync wizard with browser back/forward navigation
  useWizardUrlSync();

  return <AnimatedStep stepKey={activeStep}>{children}</AnimatedStep>;
}
